import { useState } from "react";

import {
  abcAzimuth,
  almanacExtract,
  applyDecIncrement,
  applyGhaIncrement,
  correctSextant,
  formatDeclination,
  formatDegMin,
  haversineHc,
  intercept,
  normalize360,
  sightReduction,
  type AlmanacBody,
} from "@nav-games/astro";

/// Printable sight-reduction pro forma — the worksheet the RYA exam
/// expects students to fill in.
///
/// Two modes:
///
///   - **Blank** — print an empty form, fill it in with pen and paper
///     while you work the sight.
///   - **Worked** — punch in your numbers and the calculator fills
///     every cell, showing every intermediate value the way the form
///     wants them. Use it to check your hand-working line by line.
///
/// The print CSS hides the page chrome and the input controls so a
/// browser "Print" lays out one form per A4 sheet.

interface ProFormaProps {
  /// Defaults to the current UT — pass a fixed Date for reproducible
  /// worked examples in the Reference tab.
  initialDate?: Date;
  onBack?: () => void;
}

const BODY_ORDER: readonly AlmanacBody[] = [
  "sun",
  "aries",
  "venus",
  "mars",
  "jupiter",
  "saturn",
  "moon",
];

const BODY_LABEL: Record<AlmanacBody, string> = {
  sun: "Sun",
  aries: "Aries (star)",
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
  moon: "Moon",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 36,
  padding: "6px 8px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  fontSize: 14,
};

function NumField({
  label,
  value,
  onChange,
  step = 0.1,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: number;
  hint?: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minWidth: 120,
      }}
    >
      <span className="small muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 10 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function todayIso(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export function SightReductionProForma({ initialDate, onBack }: ProFormaProps) {
  const init = initialDate ?? new Date();

  const [body, setBody] = useState<AlmanacBody>("sun");
  const [limb, setLimb] = useState<"LL" | "UL">("LL");

  const [dateStr, setDateStr] = useState(todayIso(init));
  const [hour, setHour] = useState(String(init.getUTCHours()));
  const [minute, setMinute] = useState(String(init.getUTCMinutes()));
  const [second, setSecond] = useState(String(init.getUTCSeconds()));

  const [hsDeg, setHsDeg] = useState("35");
  const [hsMin, setHsMin] = useState("47.3");
  const [ieMin, setIeMin] = useState("2.0");
  const [hoe, setHoe] = useState("3");

  const [apLat, setApLat] = useState("51.0");
  const [apLon, setApLon] = useState("0.0");

  const [mode, setMode] = useState<"blank" | "worked">("worked");

  // Parse inputs — all numeric, all required for the worked branch.
  const h = Number(hour);
  const m = Number(minute);
  const s = Number(second);
  const hs = Number(hsDeg) + Number(hsMin) / 60;
  const ie = Number(ieMin);
  const hoeM = Number(hoe);
  const lat = Number(apLat);
  const lon = Number(apLon);

  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const valid =
    mode === "blank" ||
    (validDate &&
      [h, m, s, Number(hsDeg), Number(hsMin), ie, hoeM, lat, lon].every(
        Number.isFinite,
      ));

  // Build the sight date.
  const sightDate = validDate
    ? new Date(
        `${dateStr}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}Z`,
      )
    : new Date();
  const hourStart = new Date(sightDate);
  hourStart.setUTCMinutes(0, 0, 0);

  const isLimbBody = body === "sun" || body === "moon";

  // Hs → Ho chain (only for bodies the sextant chain handles).
  const corr =
    mode === "worked" && valid
      ? correctSextant({
          body:
            body === "sun" || body === "moon"
              ? { kind: body, limb }
              : body === "aries"
                ? { kind: "star" }
                : { kind: "planet", planet: body },
          hsDeg: hs,
          indexErrorMin: ie,
          heightOfEyeM: hoeM,
          date: sightDate,
        })
      : null;

  // Almanac extract + increments for GHA / Dec at the sight instant.
  const extract = mode === "worked" && valid ? almanacExtract(body, hourStart) : null;
  const ghaSight =
    extract != null
      ? applyGhaIncrement({
          body,
          hourlyGhaDeg: extract.ghaDeg,
          m,
          s,
          vPerHourMin: extract.vPerHourMin,
        })
      : null;
  const decSight =
    extract != null
      ? applyDecIncrement({
          hourlyDecDeg: extract.decDeg,
          m,
          s,
          dPerHourMin: extract.dPerHourMin,
        })
      : null;

  // Sight reduction.
  const reduction =
    ghaSight != null && decSight != null && corr != null
      ? sightReduction({
          apLatDeg: lat,
          apLonDeg: lon,
          ghaDeg: ghaSight,
          decDeg: decSight,
          hoDeg: corr.hoDeg,
        })
      : null;
  const hav =
    reduction != null
      ? haversineHc({ latDeg: lat, decDeg: decSight!, lhaDeg: reduction.lhaDeg })
      : null;
  const abc =
    reduction != null
      ? abcAzimuth({ latDeg: lat, decDeg: decSight!, lhaDeg: reduction.lhaDeg })
      : null;
  const inter =
    reduction != null && corr != null
      ? intercept(corr.hoDeg, reduction.hcDeg)
      : null;

  const cell = (text: string | null | undefined): string =>
    mode === "worked" && text ? text : " ";

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .pro-forma, .pro-forma * { visibility: visible; }
          .pro-forma { position: absolute; left: 0; top: 0; width: 100%; }
          .pro-forma .no-print { display: none !important; }
          .pro-forma .pf-page { break-after: page; }
        }
        .pro-forma table { border-collapse: collapse; width: 100%; font-size: 13px; }
        .pro-forma td, .pro-forma th {
          border: 1px solid #888;
          padding: 4px 6px;
          vertical-align: middle;
          min-height: 24px;
        }
        .pro-forma th {
          background: #f1f5f9;
          color: #0f172a;
          text-align: left;
          font-weight: 600;
        }
        .pro-forma .label-col { width: 45%; }
        .pro-forma .value-col { width: 55%; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .pro-forma .pf-section h4 {
          margin: 12px 0 6px;
          font-size: 14px;
          color: var(--text);
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className={mode === "blank" ? "" : "secondary"}
            onClick={() => setMode("blank")}
            style={{ flex: "1 1 140px" }}
          >
            Blank form (print)
          </button>
          <button
            type="button"
            className={mode === "worked" ? "" : "secondary"}
            onClick={() => setMode("worked")}
            style={{ flex: "1 1 140px" }}
          >
            Worked form
          </button>
        </div>

        {mode === "worked" && (
          <div className="card" style={{ padding: 12 }}>
            <p
              className="small muted"
              style={{ marginTop: 0, marginBottom: 10 }}
            >
              Punch in your sight. The form below populates every cell;
              browser <strong>Print</strong> gives you a clean A4 copy.
            </p>

            <div className="small muted" style={{ marginBottom: 4 }}>
              Body
            </div>
            <div
              style={{
                display: "flex",
                gap: 4,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              {BODY_ORDER.map((b) => (
                <button
                  key={b}
                  type="button"
                  className={body === b ? "" : "secondary"}
                  onClick={() => setBody(b)}
                  style={{ flex: "1 1 80px", fontSize: 13, padding: "6px 4px" }}
                  aria-pressed={body === b}
                >
                  {BODY_LABEL[b]}
                </button>
              ))}
            </div>

            {isLimbBody && (
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                {(["LL", "UL"] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    className={limb === l ? "" : "secondary"}
                    onClick={() => setLimb(l)}
                    style={{ flex: 1 }}
                    aria-pressed={limb === l}
                  >
                    {l === "LL" ? "Lower limb" : "Upper limb"}
                  </button>
                ))}
              </div>
            )}

            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginBottom: 8,
              }}
            >
              <span className="small muted">Date (UTC)</span>
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                style={inputStyle}
                aria-label="Date"
              />
            </label>

            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <NumField label="UT h" value={hour} onChange={setHour} step={1} />
              <NumField label="UT m" value={minute} onChange={setMinute} step={1} />
              <NumField label="UT s" value={second} onChange={setSecond} step={1} />
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <NumField label="Hs deg" value={hsDeg} onChange={setHsDeg} step={1} />
              <NumField label="Hs min" value={hsMin} onChange={setHsMin} step={0.1} />
              <NumField label="IE (′)" value={ieMin} onChange={setIeMin} step={0.1} hint="+ on / − off" />
              <NumField label="HoE (m)" value={hoe} onChange={setHoe} step={0.5} />
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <NumField label="AP Lat (°)" value={apLat} onChange={setApLat} step={0.1} hint="N + / S −" />
              <NumField label="AP Lon (°)" value={apLon} onChange={setApLon} step={0.1} hint="E + / W −" />
            </div>

            <button
              type="button"
              onClick={() => window.print()}
              style={{ width: "100%", marginTop: 4 }}
            >
              🖨 Print worksheet
            </button>
          </div>
        )}

        {mode === "blank" && (
          <div className="card" style={{ padding: 12 }}>
            <p className="small muted" style={{ margin: 0 }}>
              Blank pro-forma worksheet — use your browser&apos;s{" "}
              <strong>Print</strong> to lay it out on A4 and fill it in by
              hand while you work the sight.
            </p>
            <button
              type="button"
              onClick={() => window.print()}
              style={{ width: "100%", marginTop: 8 }}
            >
              🖨 Print blank form
            </button>
          </div>
        )}
      </div>

      <div className="pro-forma">
        <div className="pf-page" style={{ padding: 16, background: "var(--bg, white)" }}>
          <h2 style={{ marginTop: 0 }}>Sight reduction — pro forma</h2>
          <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
            RYA Ocean style worksheet. Work in the order printed.
          </p>

          {/* Header — date / body / position */}
          <table className="pf-section">
            <tbody>
              <tr>
                <th className="label-col">Date (UT)</th>
                <td className="value-col">
                  {cell(mode === "worked" ? dateStr : "")}
                </td>
              </tr>
              <tr>
                <th>UT of sight (h m s)</th>
                <td className="value-col">
                  {cell(
                    mode === "worked"
                      ? `${String(h).padStart(2, "0")} ${String(m).padStart(2, "0")} ${String(s).padStart(2, "0")}`
                      : "",
                  )}
                </td>
              </tr>
              <tr>
                <th>Body</th>
                <td className="value-col">
                  {cell(
                    mode === "worked"
                      ? `${BODY_LABEL[body]}${isLimbBody ? " " + (limb === "LL" ? "LL" : "UL") : ""}`
                      : "",
                  )}
                </td>
              </tr>
              <tr>
                <th>Assumed Position (Lat / Lon)</th>
                <td className="value-col">
                  {cell(
                    mode === "worked" ? `${lat.toFixed(1)}° / ${lon.toFixed(1)}°` : "",
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Hs → Ho */}
          <div className="pf-section">
            <h4>1.  Sextant corrections (Hs → Ho)</h4>
            <table>
              <tbody>
                <tr>
                  <th className="label-col">Hs (sextant altitude)</th>
                  <td className="value-col">
                    {cell(corr ? formatDegMin(hs) : "")}
                  </td>
                </tr>
                <tr>
                  <th>− Index error ({mode === "worked" ? `${ie >= 0 ? "+" : "−"}${Math.abs(ie).toFixed(1)}′` : ""})</th>
                  <td className="value-col">
                    {cell(corr ? `${(-ie).toFixed(1)}′` : "")}
                  </td>
                </tr>
                <tr>
                  <th>− Dip (HoE = {mode === "worked" ? `${hoeM} m` : ""})</th>
                  <td className="value-col">
                    {cell(corr ? `${(-corr.dipMin).toFixed(1)}′` : "")}
                  </td>
                </tr>
                <tr>
                  <th>Ha (apparent altitude)</th>
                  <td className="value-col">
                    {cell(
                      corr
                        ? formatDegMin(hs - ie / 60 - corr.dipMin / 60)
                        : "",
                    )}
                  </td>
                </tr>
                <tr>
                  <th>− Refraction</th>
                  <td className="value-col">
                    {cell(corr ? `${(-corr.refractionMin).toFixed(1)}′` : "")}
                  </td>
                </tr>
                {isLimbBody && (
                  <tr>
                    <th>± Semi-diameter ({mode === "worked" ? `${corr ? (limb === "LL" ? "+" : "−") + corr.semiDiameterMin.toFixed(1) + "′" : ""}` : ""})</th>
                    <td className="value-col">
                      {cell(
                        corr
                          ? `${limb === "LL" ? "+" : "−"}${corr.semiDiameterMin.toFixed(1)}′`
                          : "",
                      )}
                    </td>
                  </tr>
                )}
                {(body === "sun" || body === "moon") && (
                  <tr>
                    <th>+ Parallax {body === "moon" ? "(HP · cos Ha)" : ""}</th>
                    <td className="value-col">
                      {cell(corr ? `+${corr.parallaxMin.toFixed(1)}′` : "")}
                    </td>
                  </tr>
                )}
                <tr>
                  <th>
                    <strong>Ho (observed altitude)</strong>
                  </th>
                  <td className="value-col">
                    <strong>{cell(corr ? formatDegMin(corr.hoDeg) : "")}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* GHA / Dec */}
          <div className="pf-section">
            <h4>2.  Almanac — GHA and Declination</h4>
            <table>
              <tbody>
                <tr>
                  <th className="label-col">GHA at h:00</th>
                  <td className="value-col">
                    {cell(extract ? formatDegMin(extract.ghaDeg, 1) : "")}
                  </td>
                </tr>
                <tr>
                  <th>+ Increment for {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s</th>
                  <td className="value-col">
                    {cell(
                      extract
                        ? `${(
                            (m / 60 + s / 3600) *
                            (body === "aries"
                              ? 15.04107 * 60
                              : body === "moon"
                                ? 14.31667 * 60
                                : 15.0 * 60)
                          ).toFixed(1)}′`
                        : "",
                    )}
                  </td>
                </tr>
                {extract && extract.vPerHourMin !== 0 && (
                  <tr>
                    <th>+ v-correction ({extract.vPerHourMin.toFixed(1)}′/hr)</th>
                    <td className="value-col">
                      {cell(
                        `${(extract.vPerHourMin * (m / 60 + s / 3600)).toFixed(1)}′`,
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <th>
                    <strong>GHA (sight)</strong>
                  </th>
                  <td className="value-col">
                    <strong>{cell(ghaSight != null ? formatDegMin(ghaSight, 1) : "")}</strong>
                  </td>
                </tr>
                <tr>
                  <th>± AP Longitude</th>
                  <td className="value-col">
                    {cell(mode === "worked" ? `${lon >= 0 ? "+" : "−"}${Math.abs(lon).toFixed(1)}°` : "")}
                  </td>
                </tr>
                <tr>
                  <th>
                    <strong>LHA (mod 360°)</strong>
                  </th>
                  <td className="value-col">
                    <strong>
                      {cell(
                        ghaSight != null
                          ? formatDegMin(normalize360(ghaSight + lon), 1)
                          : "",
                      )}
                    </strong>
                  </td>
                </tr>
                {body !== "aries" && (
                  <>
                    <tr>
                      <th>Dec at h:00</th>
                      <td className="value-col">
                        {cell(extract ? formatDeclination(extract.decDeg, 1) : "")}
                      </td>
                    </tr>
                    <tr>
                      <th>+ d-correction ({extract ? extract.dPerHourMin.toFixed(1) + "′/hr" : ""})</th>
                      <td className="value-col">
                        {cell(
                          extract
                            ? `${extract.dPerHourMin >= 0 ? "+" : ""}${(extract.dPerHourMin * (m / 60 + s / 3600)).toFixed(1)}′`
                            : "",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <strong>Dec (sight)</strong>
                      </th>
                      <td className="value-col">
                        <strong>{cell(decSight != null ? formatDeclination(decSight, 1) : "")}</strong>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Hc + Zn via haversine + ABC */}
          <div className="pf-section">
            <h4>3.  Sight reduction (haversine + ABC)</h4>
            <table>
              <tbody>
                <tr>
                  <th className="label-col">L − D</th>
                  <td className="value-col">
                    {cell(hav ? `${hav.latMinusDecDeg.toFixed(2)}°` : "")}
                  </td>
                </tr>
                <tr>
                  <th>hav(L − D)</th>
                  <td className="value-col">
                    {cell(hav ? hav.havLatMinusDec.toFixed(5) : "")}
                  </td>
                </tr>
                <tr>
                  <th>cos(L) · cos(D)</th>
                  <td className="value-col">
                    {cell(hav ? hav.cosLatCosDec.toFixed(5) : "")}
                  </td>
                </tr>
                <tr>
                  <th>hav(LHA)</th>
                  <td className="value-col">
                    {cell(hav ? hav.havLha.toFixed(5) : "")}
                  </td>
                </tr>
                <tr>
                  <th>
                    hav(ZD) = hav(L−D) + cos(L)·cos(D)·hav(LHA)
                  </th>
                  <td className="value-col">
                    {cell(hav ? hav.havZd.toFixed(5) : "")}
                  </td>
                </tr>
                <tr>
                  <th>ZD</th>
                  <td className="value-col">
                    {cell(hav ? formatDegMin(hav.zdDeg) : "")}
                  </td>
                </tr>
                <tr>
                  <th>
                    <strong>Hc = 90° − ZD</strong>
                  </th>
                  <td className="value-col">
                    <strong>{cell(hav ? formatDegMin(hav.hcDeg) : "")}</strong>
                  </td>
                </tr>
                <tr>
                  <th>A = tan(L)/tan(LHA), named</th>
                  <td className="value-col">
                    {cell(abc ? `${abc.a.toFixed(4)} ${abc.aName}` : "")}
                  </td>
                </tr>
                <tr>
                  <th>B = tan(D)/sin(LHA), named</th>
                  <td className="value-col">
                    {cell(abc ? `${abc.b.toFixed(4)} ${abc.bName}` : "")}
                  </td>
                </tr>
                <tr>
                  <th>C = A ± B</th>
                  <td className="value-col">
                    {cell(abc ? `${abc.c.toFixed(4)} ${abc.cName}` : "")}
                  </td>
                </tr>
                <tr>
                  <th>Z = atan( 1 / (C · cos L) )</th>
                  <td className="value-col">
                    {cell(
                      abc
                        ? `${abc.zNS} ${abc.zDeg.toFixed(1)}° ${abc.zEW}`
                        : "",
                    )}
                  </td>
                </tr>
                <tr>
                  <th>
                    <strong>Zn (true)</strong>
                  </th>
                  <td className="value-col">
                    <strong>
                      {cell(abc ? `${abc.znDeg.toFixed(1)}° T` : "")}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Intercept + plotting line */}
          <div className="pf-section">
            <h4>4.  Intercept (HoMoTo)</h4>
            <table>
              <tbody>
                <tr>
                  <th className="label-col">Ho</th>
                  <td className="value-col">
                    {cell(corr ? formatDegMin(corr.hoDeg) : "")}
                  </td>
                </tr>
                <tr>
                  <th>Hc</th>
                  <td className="value-col">
                    {cell(hav ? formatDegMin(hav.hcDeg) : "")}
                  </td>
                </tr>
                <tr>
                  <th>Ho − Hc (arc-min = nm)</th>
                  <td className="value-col">
                    {cell(
                      inter
                        ? `${inter.distanceNm.toFixed(1)} nm ${inter.direction}`
                        : "",
                    )}
                  </td>
                </tr>
                <tr>
                  <th>
                    <strong>Plot from AP along Zn = </strong>
                  </th>
                  <td className="value-col">
                    <strong>{cell(abc ? `${abc.znDeg.toFixed(1)}° T` : "")}</strong>
                  </td>
                </tr>
                <tr>
                  <th>
                    <strong>Intercept</strong>
                  </th>
                  <td className="value-col">
                    <strong>
                      {cell(
                        inter
                          ? `${inter.distanceNm.toFixed(1)} nm ${inter.direction}`
                          : "",
                      )}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="small muted" style={{ marginTop: 16 }}>
            Position line perpendicular to Zn through the intercept point.
          </p>
        </div>
      </div>

      {onBack && (
        <button
          className="secondary no-print"
          onClick={onBack}
          style={{ marginTop: 12 }}
        >
          ← Back
        </button>
      )}
    </>
  );
}
